import { useState, useCallback, useRef } from 'react';
import { useZoomAnimation } from './useZoomAnimation';
import { useWheelHandler } from './useWheelHandler';
import { useContentBounds } from './useContentBounds';
import { usePanConstraints } from './usePanConstraints';
import { useResizeHandler } from './useResizeHandler';

/**
 * useViewportConstraints Hook
 * 
 * Manages viewport constraints for pan and zoom:
 * - Calculates content bounds from node positions
 * - Constrains pan to keep content visible
 * - Constrains zoom to prevent zooming out too far
 * - Provides zoom controls (wheel, buttons)
 * - Eases pan on resize to keep content recentered (sidebar / window)
 * - Supports configurable trackpad sensitivity
 */
export function useViewportConstraints({
  containerRef,
  tasks,
  nodePositions,
  customPositions,
  nodeDimensions,
  panHook,
  zoomHook,
  isFocusModeRef,
  isPanningRef,
  trackpadPanSensitivity,
  trackpadZoomSensitivity,
}) {
  // Padding for minimap display
  const MINIMAP_PADDING = 800;
  
  // Zoom state
  const [zoom, setZoomState] = useState(1);
  
  // PERFORMANCE: Cache container rect to avoid layout thrashing during pan
  const containerRectRef = useRef({ width: 0, height: 0, left: 0, top: 0 });
  
  // Store zoom in a ref so handlers can access the latest value
  // CRITICAL: We must NOT blindly overwrite refs from state on every render!
  // The refs are the single source of truth for the current zoom/pan.
  const zoomRef = useRef(zoom);
  
  // Use panHook.panRef as the single source of truth for pan position
  // This ensures useWheelHandler and useFlowchartEvents read the same value
  const panRef = panHook.panRef;
  
  // Helper to update DOM transform directly
  const updateDOMTransform = useCallback((newPan, newZoom) => {
    const canvas = containerRef.current?.querySelector('.flowchart__canvas');
    if (canvas) {
      canvas.style.transform = `translate(${newPan.x}px, ${newPan.y}px) scale(${newZoom})`;
      canvas.style.setProperty('--inverse-zoom', 1 / newZoom);
      canvas.dispatchEvent(new CustomEvent('panupdate', { 
        detail: { x: newPan.x, y: newPan.y, zoom: newZoom } 
      }));
    }
  }, [containerRef]);
  
  // ═══════════════════════════════════════════════════════════════
  // CONTENT BOUNDS (extracted hook)
  // ═══════════════════════════════════════════════════════════════
  const { contentBounds, contentBoundsRef, rawBoundsRef } = useContentBounds({
    tasks,
    nodePositions,
    customPositions,
    nodeDimensions,
    minimapPadding: MINIMAP_PADDING,
  });

  // ═══════════════════════════════════════════════════════════════
  // MIN ZOOM CALCULATION
  // ═══════════════════════════════════════════════════════════════
  const getMinZoom = useCallback(() => {
    if (!containerRef.current || tasks.length === 0) return 0.1;
    
    const container = containerRectRef.current;
    if (container.width === 0) {
      const rect = containerRef.current.getBoundingClientRect();
      containerRectRef.current = { width: rect.width, height: rect.height, left: rect.left, top: rect.top };
    }
    const bounds = contentBoundsRef.current;
    const minZoomX = container.width / bounds.width;
    const minZoomY = container.height / bounds.height;
    
    return Math.max(Math.min(minZoomX, minZoomY), 0.1);
  }, [tasks.length, containerRef, contentBoundsRef]);

  // ═══════════════════════════════════════════════════════════════
  // PAN CONSTRAINTS (extracted hook)
  // ═══════════════════════════════════════════════════════════════
  const { constrainPan, setPan, setPanUnconstrained, setPanAnimated } = usePanConstraints({
    containerRef,
    containerRectRef,
    tasks,
    rawBoundsRef,
    zoomRef,
    panRef,
    panHook,
    isFocusModeRef,
    zoom,
    updateDOMTransform,
  });

  // ═══════════════════════════════════════════════════════════════
  // ZOOM CONTROLS
  // ═══════════════════════════════════════════════════════════════
  const setZoom = useCallback((newZoom, skipPanConstrain = false) => {
    const minZoom = getMinZoom();
    let nextZoom = typeof newZoom === 'function' ? newZoom(zoomRef.current) : newZoom;
    nextZoom = Math.max(minZoom, Math.min(2, nextZoom));
    
    // Update React state and ref
    setZoomState(nextZoom);
    zoomRef.current = nextZoom;
    
    // Also update panHook's zoomRef so its setPan uses correct zoom
    if (panHook.zoomRef) {
      panHook.zoomRef.current = nextZoom;
    }
    
    // Re-constrain pan if not skipping
    let finalPan = panRef.current;
    if (!skipPanConstrain) {
      finalPan = constrainPan(panRef.current, nextZoom);
      panRef.current = finalPan;
      panHook.setPan(finalPan, true);
    }
    
    updateDOMTransform(finalPan, nextZoom);
  }, [panHook, constrainPan, getMinZoom, updateDOMTransform]);

  // ═══════════════════════════════════════════════════════════════
  // ZOOM ANIMATION (extracted hook)
  // ═══════════════════════════════════════════════════════════════
  const zoomAnimation = useZoomAnimation({
    containerRef,
    zoomRef,
    panRef,
    constrainPan,
    setZoomState,
    panHook,
    getMinZoom,
  });
  
  // Sync container rect ref with animation hook
  zoomAnimation.containerRectRef.current = containerRectRef.current;

  // ═══════════════════════════════════════════════════════════════
  // WHEEL HANDLER (extracted hook)
  // ═══════════════════════════════════════════════════════════════
  useWheelHandler({
    containerRef,
    containerRectRef,
    zoomRef,
    panRef,
    constrainPan,
    panHook,
    setZoomState,
    getMinZoom,
    queueZoomToPoint: zoomAnimation.queueZoomToPoint,
    animatingZoomRef: zoomAnimation.animatingZoomRef,
    targetZoomRef: zoomAnimation.targetZoomRef,
    isPanningRef,
    trackpadPanSensitivity,
    trackpadZoomSensitivity,
  });

  // ═══════════════════════════════════════════════════════════════
  // NAVIGATION HANDLERS
  // ═══════════════════════════════════════════════════════════════
  const handleResetView = useCallback(() => {
    panHook.handleResetView(setZoom);
  }, [panHook, setZoom]);

  const handleGoToStart = useCallback(() => {
    panHook.handleGoToStart(setZoom, zoom);
  }, [panHook, setZoom, zoom]);

  // Instant version for view switching (no animation)
  const handleGoToStartInstant = useCallback(() => {
    panHook.handleGoToStartInstant(setZoom);
  }, [panHook, setZoom]);

  // ═══════════════════════════════════════════════════════════════
  // RESIZE HANDLING (extracted hook)
  // ═══════════════════════════════════════════════════════════════
  const { skipResizeAdjustment } = useResizeHandler({
    containerRef,
    containerRectRef,
    panHook,
  });

  return {
    zoom,
    zoomRef,
    setZoom,
    setPan,
    setPanAnimated,
    setPanUnconstrained,
    contentBounds,
    contentBoundsRef,
    containerRectRef,
    constrainPan,
    getMinZoom,
    handleWheel: () => {},
    handleZoomIn: zoomAnimation.handleZoomIn,
    handleZoomOut: zoomAnimation.handleZoomOut,
    cancelPanCorrection: zoomAnimation.cancelPanCorrection,
    handleResetView,
    handleGoToStart,
    handleGoToStartInstant,
    skipResizeAdjustment,
    isAnimatingPan: panHook.isAnimatingPan, // True during camera pan animations
  };
}

export default useViewportConstraints;
